import {
  FieldPath,
  FieldPathValue,
  useForm,
  UseFormReturn,
} from "react-hook-form";

import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import { OTHER_STATUS_MAP, OtherStatusEnum } from "@/domain/applications";
import {
  SPECIAL_CONDITIONS_MAP,
  SpecialConditionsEnum,
} from "@/domain/eligibility";
import { HouseholdMemberType } from "@/domain/householdMember";

type SpecialConditionsFieldKeyType =
  | SpecialConditionsEnum
  | "pregnantBabiesExpected"
  | "pregnantDueDate";

type SetNestedValueParamsType = {
  memberId: string;
  field: SpecialConditionsFieldKeyType;
  value: string | null;
};

type WatchNestedValueParamsType = {
  memberId: string;
  field: SpecialConditionsFieldKeyType;
};

type StatusMapType<T extends string> = Record<
  T,
  { active: string; inactive: string }
>;

type MemberStatusType<T extends string> = Record<T, string | null>;

type MembersStatusFormType<T extends string> = Record<
  string,
  MemberStatusType<T>
>;

type SpecialConditionsMemberStatusType =
  MemberStatusType<SpecialConditionsEnum> & {
    pregnantBabiesExpected: string | null;
    pregnantDueDate: string | null;
  };

type SpecialConditionsFormType = Record<
  string,
  SpecialConditionsMemberStatusType
>;

export type OtherStatusFormType = MembersStatusFormType<OtherStatusEnum>;

type BuildDefaultValuesParamsType<T extends string> = {
  members: HouseholdMemberType[] | undefined;
  statusEnum: Record<string, T>;
  statusMap: StatusMapType<T>;
  includePregnancyFields?: boolean;
};

const buildDefaultValues = <T extends string>({
  members,
  statusEnum,
  statusMap,
  includePregnancyFields = false,
}: BuildDefaultValuesParamsType<T>): MembersStatusFormType<T> => {
  if (!members) {
    return {};
  }

  return members.reduce<MembersStatusFormType<T>>((acc, member) => {
    const memberValues = Object.values(statusEnum).reduce(
      (statusAcc, status) => {
        const memberValue = member[status as keyof HouseholdMemberType];
        statusAcc[status] =
          (memberValue as string) ?? statusMap[status]?.inactive ?? null;
        return statusAcc;
      },
      {} as MemberStatusType<T>,
    );

    if (includePregnancyFields) {
      (
        memberValues as SpecialConditionsMemberStatusType
      ).pregnantBabiesExpected = member.pregnantBabiesExpected
        ? String(member.pregnantBabiesExpected)
        : null;
      (memberValues as SpecialConditionsMemberStatusType).pregnantDueDate =
        member.pregnantDueDate ?? null;
    }

    acc[member.id] = memberValues;
    return acc;
  }, {});
};

type UseSpecialConditionsFormReturnType = {
  form: UseFormReturn<SpecialConditionsFormType>;
  householdMembers: HouseholdMemberType[] | undefined;
  setNestedValue: (params: SetNestedValueParamsType) => void;
  watchNestedValue: (params: WatchNestedValueParamsType) => string | null;
};

export const useSpecialConditionsForm =
  (): UseSpecialConditionsFormReturnType => {
    const { data: householdMembers } = useListHouseholdMembersQuery();

    const form = useForm<SpecialConditionsFormType>({
      defaultValues: buildDefaultValues({
        members: householdMembers,
        statusEnum: SpecialConditionsEnum,
        statusMap: SPECIAL_CONDITIONS_MAP,
        includePregnancyFields: true,
      }) as SpecialConditionsFormType,
    });

    const setNestedValue = ({
      memberId,
      field,
      value,
    }: SetNestedValueParamsType) => {
      const path: FieldPath<SpecialConditionsFormType> = `${memberId}.${field}`;
      form.setValue(
        path,
        value as unknown as FieldPathValue<
          SpecialConditionsFormType,
          typeof path
        >,
      );
    };

    const watchNestedValue = ({
      memberId,
      field,
    }: WatchNestedValueParamsType): string | null => {
      const path: FieldPath<SpecialConditionsFormType> = `${memberId}.${field}`;
      return form.watch(path) as unknown as string | null;
    };

    return {
      form,
      householdMembers,
      setNestedValue,
      watchNestedValue,
    };
  };

type UseOtherStatusFormReturnType = {
  form: UseFormReturn<OtherStatusFormType>;
  householdMembers: HouseholdMemberType[] | undefined;
};

export const useOtherStatusForm = (): UseOtherStatusFormReturnType => {
  const { data: householdMembers } = useListHouseholdMembersQuery();

  const form = useForm<OtherStatusFormType>({
    defaultValues: buildDefaultValues({
      members: householdMembers,
      statusEnum: OtherStatusEnum,
      statusMap: OTHER_STATUS_MAP,
    }),
  });

  return {
    form,
    householdMembers,
  };
};
